import { NgClass } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  OnDestroy,
  output,
  signal,
  untracked,
  viewChild,
  WritableSignal
} from '@angular/core';
import { autocompletion, closeBrackets, startCompletion } from '@codemirror/autocomplete';
import { linter, lintGutter } from '@codemirror/lint';
import { EditorState } from '@codemirror/state';
import { EditorView, keymap, ViewUpdate } from '@codemirror/view';
import { TranslateService } from '@ngx-translate/core';

import { formulaAutocompletionSource } from '../../helpers/formula-autocomplete';
import { variableDefaultHighlight, variableOccurrenceHighlight } from '../../helpers/formula-highlight';
import { formulaLanguage } from '../../helpers/formula-language';
import { FormulaValidationResult } from '../../models/formula-result.model';
import { DEFAULT_FORMULA_EDITOR_CONFIG, FormulaEditorConfig } from '../../utils/formula-editor-config';
import { formulaLintSource, validateFormula } from '../../utils/formula-validation';

@Component({
  selector: 'iot-platform-ui-formula-editor',
  imports: [NgClass],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="formula-editor-container">
      <div
        #editorHost
        class="formula-editor-host"
        [ngClass]="{
          'has-error': !formula()?.length
        }"
      ></div>
      @if (translatedError(); as errorMsg) {
        <div class="formula-editor-error" data-test="formula-editor-error">
          <span class="formula-editor-error-icon">⚠</span>
          <span>{{ errorMsg }}</span>
        </div>
      }
    </div>
  `,
  styles: `
    .formula-editor-container {
      display: flex;
      flex-direction: column;
      gap: 8px;
      width: 100%;
    }

    .formula-editor-host {
      border: 1px solid var(--mat-sys-outline-variant, #c4c7c5);
      border-radius: 8px;
      overflow: hidden;
      font-family: 'JetBrains Mono', 'Fira Code', 'Consolas', monospace;
      font-size: 14px;
      line-height: 1.6;
    }

    .formula-editor-host:focus-within {
      border-color: var(--mat-sys-primary, #6750a4);
      box-shadow: 0 0 0 1px var(--mat-sys-primary, #6750a4);
    }

    .formula-editor-host.has-error {
      border-color: var(--mat-sys-error, #b3261e);
    }

    .formula-editor-error {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 6px 12px;
      border-radius: 6px;
      font-size: 12px;
      color: var(--mat-sys-error, #b3261e);
      background: var(--mat-sys-error-container, #f9dedc);
    }

    .formula-editor-error-icon {
      font-size: 14px;
      flex-shrink: 0;
    }

    /* CodeMirror theme overrides */
    :host ::ng-deep .cm-editor {
      outline: none;
    }

    :host ::ng-deep .cm-editor .cm-content {
      padding: 12px;
      font-family: 'JetBrains Mono', 'Fira Code', 'Consolas', monospace;
    }

    :host ::ng-deep .cm-editor .cm-gutters {
      display: none;
    }

    :host ::ng-deep .cm-editor .cm-line {
      padding: 2px 0;
    }

    /* Syntax highlighting: variables */
    :host ::ng-deep .cm-editor .ͼ-variableName,
    :host ::ng-deep .cm-editor .tok-variableName {
      color: #2196f3;
      font-weight: 600;
    }

    /* Syntax highlighting: unknown variables */
    :host ::ng-deep .cm-editor .ͼ-unknownVariable,
    :host ::ng-deep .cm-editor .tok-unknownVariable {
      color: #f44336;
      text-decoration: wavy underline #f44336;
      font-weight: 600;
    }

    /* Syntax highlighting: operators */
    :host ::ng-deep .cm-editor .ͼ-operator,
    :host ::ng-deep .cm-editor .tok-operator {
      color: #ff9800;
      font-weight: 700;
    }

    /* Syntax highlighting: numbers */
    :host ::ng-deep .cm-editor .ͼ-number,
    :host ::ng-deep .cm-editor .tok-number {
      color: #4caf50;
      font-weight: 600;
    }

    /* Syntax highlighting: math functions */
    :host ::ng-deep .cm-editor .ͼ-functionName,
    :host ::ng-deep .cm-editor .tok-functionName {
      color: #9c27b0;
      font-weight: 700;
    }

    /* Syntax highlighting: math constants */
    :host ::ng-deep .cm-editor .ͼ-constantName,
    :host ::ng-deep .cm-editor .tok-constantName {
      color: #00897b;
      font-weight: 700;
      font-style: italic;
    }

    /* Syntax highlighting: disallowed constants */
    :host ::ng-deep .cm-editor .ͼ-disallowedConstant,
    :host ::ng-deep .cm-editor .tok-disallowedConstant {
      color: #f44336;
      text-decoration: wavy underline #f44336;
      font-weight: 700;
      font-style: italic;
    }

    /* Syntax highlighting: string literals */
    :host ::ng-deep .cm-editor .ͼ-string,
    :host ::ng-deep .cm-editor .tok-string {
      color: #e65100;
    }

    /* Syntax highlighting: disallowed operators */
    :host ::ng-deep .cm-editor .ͼ-disallowedOperator,
    :host ::ng-deep .cm-editor .tok-disallowedOperator {
      color: #f44336;
      text-decoration: wavy underline #f44336;
      font-weight: 700;
    }

    /* Syntax highlighting: disallowed functions */
    :host ::ng-deep .cm-editor .ͼ-disallowedFunction,
    :host ::ng-deep .cm-editor .tok-disallowedFunction {
      color: #f44336;
      text-decoration: wavy underline #f44336;
      font-weight: 700;
    }

    /* Autocomplete dropdown */
    :host ::ng-deep .cm-tooltip-autocomplete {
      border-radius: 8px;
      border: 1px solid var(--mat-sys-outline-variant, #c4c7c5);
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
      font-family: 'JetBrains Mono', 'Fira Code', 'Consolas', monospace;
      font-size: 13px;
    }

    :host ::ng-deep .cm-tooltip-autocomplete ul li {
      padding: 4px 12px;
    }

    :host ::ng-deep .cm-tooltip-autocomplete ul li[aria-selected] {
      background: var(--mat-sys-primary-container, #eaddff);
      color: var(--mat-sys-on-primary-container, #21005d);
    }

    /* Lint error gutter */
    :host ::ng-deep .cm-lint-marker-error {
      display: none;
    }

    /* Variable occurrence highlight — default (always-on, every known variable) */
    :host ::ng-deep .formula-highlight-default {
      background: var(--mat-sys-tertiary-container, #ffd8e4);
      border-radius: 3px;
    }

    /* Variable occurrence highlight — active when cursor is on a known variable */
    :host ::ng-deep .formula-highlight-match {
      background: var(--mat-sys-primary-container, #eaddff);
      border-radius: 3px;
      outline: 1px solid var(--mat-sys-primary, #6750a4);
    }
  `
})
export class FormulaEditorComponent implements AfterViewInit, OnDestroy {
  /** List of predefined variable names for autocomplete and validation */
  readonly variables = input.required<string[]>();

  /** Initial formula string */
  readonly formula = input<string>('');

  /** Editor configuration for operator/function restrictions */
  readonly config = input<FormulaEditorConfig>(DEFAULT_FORMULA_EDITOR_CONFIG);

  /** Optional maximum allowed formula length in characters */
  readonly maxLength = input<number | undefined>(undefined);

  /** Emits the formula string on every change */
  readonly formulaChange = output<string>();

  /** Emits the validation result on every change */
  readonly validationChange = output<FormulaValidationResult>();

  readonly editorHost = viewChild.required<ElementRef<HTMLElement>>('editorHost');

  readonly validationResult: WritableSignal<FormulaValidationResult | null> = signal(null);
  private editorView: EditorView | null = null;
  private readonly injector: Injector = inject(Injector);
  private readonly translateService: TranslateService = inject(TranslateService);
  /** Translated error message for display in the template. */
  readonly translatedError = computed(() => {
    const result = this.validationResult();
    if (!result || result.valid) return null;
    if (result.errorKey) {
      return this.translateService.instant(result.errorKey, result.errorParams);
    }
    return result.error;
  });
  /** Guard flag to prevent feedback loop when syncing formula input → editor */
  private syncing = false;

  constructor() {
    // Reactively sync the formula input to the CodeMirror editor.
    // Handles late input binding when the component is created inside @if
    // (e.g., switching from visual to text mode in the dialog).
    effect(() => {
      const incoming = this.formula();
      if (this.editorView) {
        const current = this.editorView.state.doc.toString();
        if (incoming !== current) {
          this.syncing = true;
          this.editorView.dispatch({
            changes: { from: 0, to: this.editorView.state.doc.length, insert: incoming }
          });
          this.syncing = false;

          // Run validation for the synced content since updateListener is suppressed
          const variableNames = this.variables();
          const editorConfig = this.config();
          const result = validateFormula(incoming, variableNames, editorConfig, this.maxLength());
          this.validationResult.set(result);
          this.validationChange.emit(result);
        }
      }
    });
  }

  ngAfterViewInit(): void {
    effect(
      () => {
        this.destroy();
        this.initEditor();
      },
      { injector: this.injector }
    );
  }

  ngOnDestroy(): void {
    this.destroy();
  }

  private destroy(): void {
    this.editorView?.destroy();
    this.editorView = null;
  }

  private initEditor(): void {
    const variableNames = this.variables();
    untracked(() => {
      const variableSet = new Set(variableNames);
      const editorConfig = this.config();
      const initialFormula = this.formula();

      const updateListener = EditorView.updateListener.of((update: ViewUpdate) => {
        if (update.docChanged && !this.syncing) {
          const doc = update.state.doc.toString();
          this.formulaChange.emit(doc);
          const result = validateFormula(doc, variableNames, editorConfig, this.maxLength());
          this.validationResult.set(result);
          this.validationChange.emit(result);
        }
      });

      /**
       * Intercept `&` and `|` keystrokes to force the autocomplete popup.
       *
       * WHY domEventHandlers instead of inputHandler:
       *  - `activateOnTyping: true` only fires for word characters; `&`/`|` are
       *    punctuation so CodeMirror never auto-opens the popup for them.
       *  - We let CodeMirror insert the character **normally** (no preventDefault),
       *    then defer `startCompletion` to the next tick so it runs AFTER the
       *    document update (and any synchronous validation) has fully settled.
       *
       * The autocomplete source intercepts `&+` → suggests `and`
       *                                    `|+` → suggests `or`.
       */

      const state = EditorState.create({
        doc: initialFormula,
        extensions: [
          keymap.of([{ key: 'Ctrl-Space', run: startCompletion }]),
          formulaLanguage(variableSet, editorConfig),
          autocompletion({
            override: [formulaAutocompletionSource(variableNames, editorConfig)],
            activateOnTyping: true,
            icons: false
          }),
          closeBrackets(),
          linter(formulaLintSource(variableNames, editorConfig, this.maxLength()), { delay: 300 }),
          lintGutter(),
          updateListener,
          variableDefaultHighlight(variableSet),
          variableOccurrenceHighlight(variableSet),
          EditorView.lineWrapping,
          EditorView.theme({
            '&': { backgroundColor: 'transparent' },
            '.cm-content': { caretColor: 'var(--mat-sys-on-surface, #1d1b20)' },
            '.cm-cursor': { borderLeftColor: 'var(--mat-sys-on-surface, #1d1b20)' },
            '&.cm-focused': { outline: 'none' }
          })
        ]
      });

      this.editorView = new EditorView({
        state,
        parent: this.editorHost().nativeElement
      });

      // Run initial validation
      if (initialFormula) {
        const result = validateFormula(initialFormula, variableNames, editorConfig, this.maxLength());
        this.validationResult.set(result);
        this.validationChange.emit(result);
      }
    });
  }
}
